'use client';

import { useTranslations } from 'next-intl';
import type { ArticleCardResolved } from './types';
import { Link } from '@/components/Link';
import { useMagazineHref } from '@/lib/magazine/useMagazineHref';
import { useRenderContext } from '@/lib/render/RenderContextProvider';

/**
 * The hero's Article Card: one editor-picked article inside a hero's `content`
 * zone, the slot `widgets.location-card` occupies on Location Pages
 * (spec 10.03.02.02.01).
 *
 * All four fields come through `resolved`: the shared `BlockAdapter` gives a
 * lookup widget's component nothing but its resolved payload, so the resolver
 * echoes the editor's `heading`, `ctaLabel` and `ctaLink` alongside the article.
 *
 * § AC 3 fall-through: with no article resolved (no pick, unpublished, wrong
 * domain — or a brand that serves no article route) the heading still renders;
 * without a heading either the widget renders nothing at all. The hero keeps
 * its background image either way.
 *
 * `ctaLabel`/`ctaLink` stay on the CMS schema but are deliberately not
 * rendered: the design has no CTA strip under the card.
 */
export const ArticleCardComponent = ({
  resolved,
}: {
  resolved: ArticleCardResolved;
}) => {
  const t = useTranslations('magazine');
  const magazineHref = useMagazineHref();
  const { substitutePlaceholders: sub } = useRenderContext();
  const { heading, article } = resolved;
  const articleHref = article
    ? magazineHref('article', article.slug)
    : undefined;

  if (!article && !heading) {
    return null;
  }

  return (
    <div className="w-full lg:max-w-[420px]">
      {/* Square bottom corners only where the card is flush with the hero's
          bottom edge, which is from `lg` up (`lg:self-end` on the hero's slot
          against a container with `lg:pb-0`). Below that the hero pulls its
          content up over the image and the card overhangs, so its bottom
          corners stand free on the page background and are rounded. */}
      <div className="bg-secondary-lighter rounded-2xl p-8 text-center shadow-lg lg:rounded-b-none">
        {heading && (
          <p className="text-primary-dark mb-4 text-3xl font-bold">
            {sub(heading)}
          </p>
        )}
        {article && (
          <>
            <h2 className="mb-3 text-lg font-semibold">{article.title}</h2>
            {/* Clamped in CSS: the hero has a fixed height, so a long intro
                must not push the link out of the overlay. */}
            <p className="line-clamp-6 text-sm">{article.intro}</p>
            {articleHref && (
              <Link
                href={articleHref}
                className="text-primary mt-4 inline-block font-bold"
              >
                {t('readMoreFeatured')}
              </Link>
            )}
          </>
        )}
      </div>
    </div>
  );
};
